import "./header.css";

import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { CONTACT_DETAILS, NAV_LINKS, ROUTES } from "../../data/content";
import { LineIcon } from "../general/Icons";
import SmartLink from "../general/SmartLink";
import SocialIcons from "../general/SocialIcons";

const isGroupActive = (group, pathname) => group.children.some((child) => child.path === pathname);

const Caret = () => (
  <svg
    className={"nav-caret"}
    viewBox={"0 0 10 6"}
    aria-hidden={"true"}
  >
    <path d={"M0 0h10L5 6z"} />
  </svg>
);

const NavItem = ({ link, className, onNavigate }) => (link.path ? (
  <NavLink
    to={link.path}
    end
    className={className}
    onClick={onNavigate}
  >
    {link.label}
  </NavLink>
) : (
  <SmartLink
    href={link.href}
    className={className}
    onClick={onNavigate}
  >
    {link.label}
  </SmartLink>
));

//Desktop menu drop-down (Weddings / Functions). Opens on hover like Wix, and also on click / keyboard
const DropDown = ({ group, pathname }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef(null);
  const id = `menu-${group.label.toLowerCase()}`;

  React.useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        ref.current.querySelector("button").focus();
      }
    };
    const onClick = (e) => {
      if (!ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("click", onClick);
    };
  }, [isOpen]);

  return (
    <li
      ref={ref}
      className={`site-nav-group ${isOpen ? "is-open" : ""}`}
      onMouseLeave={() => setIsOpen(false)}
      onBlur={(e) => {
        if (!ref.current.contains(e.relatedTarget)) {
          setIsOpen(false);
        }
      }}
    >
      <button
        type={"button"}
        className={`site-nav-link ${isGroupActive(group, pathname) ? "active" : ""}`}
        aria-expanded={isOpen}
        aria-controls={id}
        onClick={() => setIsOpen(!isOpen)}
      >
        {group.label}
        <Caret />
      </button>
      <ul
        id={id}
        className={"site-nav-dropdown"}
      >
        {group.children.map((child) => (
          <li key={child.label}>
            <NavItem
              link={child}
              className={"site-nav-dropdown-link"}
            />
          </li>
        ))}
      </ul>
    </li>
  );
};

//Mobile menu group - expands in place
const MobileGroup = ({ group, pathname, onNavigate }) => {
  const [isOpen, setIsOpen] = React.useState(isGroupActive(group, pathname));
  const id = `mobile-menu-${group.label.toLowerCase()}`;
  return (
    <li className={`mobile-nav-group ${isOpen ? "is-open" : ""}`}>
      <button
        type={"button"}
        className={`mobile-nav-link ${isGroupActive(group, pathname) ? "active" : ""}`}
        aria-expanded={isOpen}
        aria-controls={id}
        onClick={() => setIsOpen(!isOpen)}
      >
        {group.label}
        <Caret />
      </button>
      <ul
        id={id}
        className={"mobile-nav-sublist"}
        hidden={!isOpen}
      >
        {group.children.map((child) => (
          <li key={child.label}>
            <NavItem
              link={child}
              className={"mobile-nav-sublink"}
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </li>
  );
};

//White header: logo with the social icons / phone / email on the right, and the menu underneath (as on Wix).
//The menu bar stays at the top of the window as the page scrolls. On phones the menu opens from a button
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { pathname } = useLocation();
  const toggleRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    //Stop the page behind scrolling, and move focus into the menu
    document.body.style.overflow = "hidden";
    closeRef.current.focus();

    const toggle = toggleRef.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        return;
      }
      //Keep Tab inside the menu while it's open
      if (e.key === "Tab") {
        const focusable = [...menuRef.current.querySelectorAll("a, button")].filter((el) => el.offsetParent !== null);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      toggle.focus({ preventScroll: true });
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={"site-header"}>
      <div className={"site-header-top"}>
        <Link
          to={ROUTES.HOME}
          className={"site-logo"}
          aria-label={"Estia Venues - home"}
        >
          <img
            src={"/images/logo.png"}
            alt={""}
            width={600}
            height={202}
          />
        </Link>

        <div className={"site-header-contact"}>
          <SocialIcons className={"site-header-social"} />
          <a
            href={CONTACT_DETAILS.LANDLINE.link}
            className={"site-header-detail"}
          >
            <LineIcon name={"phone"} />
            {CONTACT_DETAILS.LANDLINE.header}
          </a>
          <a
            href={`mailto:${CONTACT_DETAILS.EMAIL}`}
            className={"site-header-detail"}
          >
            <LineIcon name={"mail"} />
            {CONTACT_DETAILS.EMAIL}
          </a>
        </div>

        <button
          ref={toggleRef}
          type={"button"}
          className={"menu-toggle"}
          aria-expanded={isMenuOpen}
          aria-controls={"site-menu"}
          aria-label={"Open menu"}
          onClick={() => setIsMenuOpen(true)}
        >
          <span aria-hidden={"true"} />
          <span aria-hidden={"true"} />
          <span aria-hidden={"true"} />
        </button>
      </div>

      <nav
        className={"site-nav"}
        aria-label={"Main"}
      >
        <ul className={"site-nav-list"}>
          {NAV_LINKS.map((link) => (link.children ? (
            <DropDown
              key={link.label}
              group={link}
              pathname={pathname}
            />
          ) : (
            <li key={link.label}>
              <NavItem
                link={link}
                className={"site-nav-link"}
              />
            </li>
          )))}
        </ul>
      </nav>

      {/* Full-screen menu on phones & tablets */}
      <div
        ref={menuRef}
        id={"site-menu"}
        className={"mobile-menu"}
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Menu"}
        hidden={!isMenuOpen}
      >
        <div className={"mobile-menu-bar"}>
          <Link
            to={ROUTES.HOME}
            className={"site-logo"}
            aria-label={"Estia Venues - home"}
            onClick={closeMenu}
          >
            <img
              src={"/images/logo.png"}
              alt={""}
              width={600}
              height={202}
            />
          </Link>
          <button
            ref={closeRef}
            type={"button"}
            className={"menu-close"}
            aria-label={"Close menu"}
            onClick={closeMenu}
          >
            <svg
              viewBox={"0 0 24 24"}
              aria-hidden={"true"}
            >
              <path d={"M5 5l14 14M19 5L5 19"} />
            </svg>
          </button>
        </div>
        <nav aria-label={"Main"}>
          <ul className={"mobile-nav-list"}>
            <li>
              <NavLink
                to={ROUTES.HOME}
                end
                className={"mobile-nav-link"}
                onClick={closeMenu}
              >
                Home
              </NavLink>
            </li>
            {NAV_LINKS.map((link) => (link.children ? (
              <MobileGroup
                key={link.label}
                group={link}
                pathname={pathname}
                onNavigate={closeMenu}
              />
            ) : (
              <li key={link.label}>
                <NavItem
                  link={link}
                  className={"mobile-nav-link"}
                  onNavigate={closeMenu}
                />
              </li>
            )))}
          </ul>
        </nav>
        <div className={"mobile-menu-contact"}>
          <a href={CONTACT_DETAILS.LANDLINE.link}>
            <LineIcon name={"phone"} />
            {CONTACT_DETAILS.LANDLINE.header}
          </a>
          <a href={`mailto:${CONTACT_DETAILS.EMAIL}`}>
            <LineIcon name={"mail"} />
            {CONTACT_DETAILS.EMAIL}
          </a>
          <SocialIcons className={"mobile-menu-social"} />
        </div>
      </div>
    </header>
  );
};

export default Header;
